import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { User, Phone, CheckCircle, Loader2, AlertCircle } from "lucide-react";
import { apiClient } from "../services/api";

const PROVIDER_LABELS = { google: "Google", microsoft: "Microsoft" };

const inputStyle = {
  backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)",
  color: "var(--lm-ink)", borderRadius: "8px", padding: "10px 12px",
  fontSize: "14px", width: "100%", outline: "none", transition: "border-color 0.15s",
};
const inputErrorStyle = { ...inputStyle, borderColor: "var(--lm-error)" };
const labelStyle = {
  display: "block", fontSize: "12px", fontWeight: "500",
  marginBottom: "6px", color: "var(--lm-ink-2)",
};

export default function CompleteProfile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const provider = searchParams.get("provider") || "";
  const providerLabel = PROVIDER_LABELS[provider] || "SSO";

  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [done, setDone] = useState(false);

  // Pre-fill from stored user_data
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("user_data") || "{}");
      setForm({
        first_name: stored.first_name || stored.prenom || "",
        last_name: stored.last_name || stored.nom || "",
        phone: stored.phone || "",
      });
    } catch {}
  }, []);

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = t("completeProfile.firstNameRequired", "Prénom requis");
    if (!form.last_name.trim()) e.last_name = t("completeProfile.lastNameRequired", "Nom requis");
    if (form.phone.trim()) {
      const digits = form.phone.replace(/[\s+()-]/g, "");
      if (digits.length < 8) e.phone = t("completeProfile.phoneInvalid", "Numéro invalide (min 8 chiffres)");
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
    if (errors[field]) setErrors((p) => ({ ...p, [field]: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setSubmitError(null);
    try {
      await apiClient.put("v1/users/me/", {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        phone: form.phone.trim() || null,
      });
      setDone(true);
      setTimeout(() => window.location.replace("/dashboard"), 1500);
    } catch (err) {
      setSubmitError(err?.response?.data?.detail || t("completeProfile.saveError", "Erreur lors de la sauvegarde"));
    } finally {
      setLoading(false);
    }
  };

  const onFocus = (e) => (e.target.style.borderColor = "#5e6ad2");
  const onBlur = (e, hasError) => (e.target.style.borderColor = hasError ? "var(--lm-error)" : "var(--lm-border)");

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "var(--lm-canvas)" }}>
        <div className="text-center space-y-3">
          <CheckCircle className="w-12 h-12 mx-auto" style={{ color: "var(--lm-success)" }} />
          <p className="text-sm font-medium" style={{ color: "var(--lm-ink)" }}>
            {t("completeProfile.done", "Profil complété !")}
          </p>
          <p className="text-xs" style={{ color: "var(--lm-ink-2)" }}>
            {t("completeProfile.redirecting", "Redirection vers le tableau de bord...")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: "var(--lm-canvas)" }}>
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/assets/brand/mawered-navbar-lockup.svg" alt="Mawered" style={{ height: "26px", width: "auto", margin: "0 auto 20px" }} />
          <h1 className="text-2xl font-bold mb-1.5" style={{ color: "var(--lm-ink)", letterSpacing: "-0.6px" }}>
            {t("completeProfile.title", "Complétez votre profil")}
          </h1>
          <p className="text-sm" style={{ color: "var(--lm-ink-2)" }}>
            {t("completeProfile.subtitle", "Connecté via {{provider}}. Quelques informations supplémentaires.", { provider: providerLabel })}
          </p>
        </div>

        {/* Card */}
        <div className="rounded-2xl" style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)" }}>
          <div className="p-6 pb-4" style={{ borderBottom: "1px solid var(--lm-border)" }}>
            <h2 className="text-base font-semibold" style={{ color: "var(--lm-ink)" }}>
              {t("completeProfile.cardTitle", "Vos informations")}
            </h2>
            <p className="text-xs mt-0.5" style={{ color: "var(--lm-ink-2)" }}>
              {t("completeProfile.cardSubtitle", "Ces données seront utilisées dans toute la plateforme.")}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {submitError && (
              <div className="flex items-start gap-3 p-3 rounded-xl" style={{ backgroundColor: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)" }}>
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "var(--lm-error)" }} />
                <p className="text-sm" style={{ color: "var(--lm-error)" }}>{submitError}</p>
              </div>
            )}

            {/* First name */}
            <div>
              <label style={labelStyle}>
                <User className="w-3.5 h-3.5 inline mr-1.5" style={{ color: "#5e6ad2" }} />
                {t("completeProfile.firstName", "Prénom")} *
              </label>
              <input
                type="text"
                value={form.first_name}
                onChange={(e) => handleChange("first_name", e.target.value)}
                placeholder={t("completeProfile.firstNamePlaceholder", "Votre prénom")}
                disabled={loading}
                style={errors.first_name ? inputErrorStyle : inputStyle}
                onFocus={onFocus}
                onBlur={(e) => onBlur(e, !!errors.first_name)}
              />
              {errors.first_name && (
                <p className="mt-1 text-xs flex items-center gap-1" style={{ color: "var(--lm-error)" }}>
                  <AlertCircle className="w-3 h-3" />{errors.first_name}
                </p>
              )}
            </div>

            {/* Last name */}
            <div>
              <label style={labelStyle}>
                <User className="w-3.5 h-3.5 inline mr-1.5" style={{ color: "#5e6ad2" }} />
                {t("completeProfile.lastName", "Nom")} *
              </label>
              <input
                type="text"
                value={form.last_name}
                onChange={(e) => handleChange("last_name", e.target.value)}
                placeholder={t("completeProfile.lastNamePlaceholder", "Votre nom de famille")}
                disabled={loading}
                style={errors.last_name ? inputErrorStyle : inputStyle}
                onFocus={onFocus}
                onBlur={(e) => onBlur(e, !!errors.last_name)}
              />
              {errors.last_name && (
                <p className="mt-1 text-xs flex items-center gap-1" style={{ color: "var(--lm-error)" }}>
                  <AlertCircle className="w-3 h-3" />{errors.last_name}
                </p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label style={labelStyle}>
                <Phone className="w-3.5 h-3.5 inline mr-1.5" style={{ color: "#5e6ad2" }} />
                {t("completeProfile.phone", "Téléphone")}
                <span className="ml-1" style={{ color: "var(--lm-ink-3)" }}>({t("common.optional", "optionnel")})</span>
              </label>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => handleChange("phone", e.target.value)}
                placeholder="+216 XX XXX XXX"
                disabled={loading}
                style={errors.phone ? inputErrorStyle : inputStyle}
                onFocus={onFocus}
                onBlur={(e) => onBlur(e, !!errors.phone)}
              />
              {errors.phone && (
                <p className="mt-1 text-xs flex items-center gap-1" style={{ color: "var(--lm-error)" }}>
                  <AlertCircle className="w-3 h-3" />{errors.phone}
                </p>
              )}
            </div>

            <div style={{ paddingTop: "4px" }}>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 rounded-lg text-sm font-semibold transition-colors flex items-center justify-center gap-2"
                style={{ backgroundColor: "#5e6ad2", color: "#fff", opacity: loading ? 0.6 : 1 }}
                onMouseEnter={(e) => { if (!loading) e.currentTarget.style.backgroundColor = "#828fff"; }}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#5e6ad2")}
              >
                {loading
                  ? <><Loader2 className="w-4 h-4 animate-spin" />{t("completeProfile.saving", "Enregistrement...")}</>
                  : t("completeProfile.submit", "Accéder au tableau de bord")}
              </button>

              <button
                type="button"
                className="w-full mt-3 text-xs text-center"
                style={{ color: "var(--lm-ink-3)" }}
                onClick={() => window.location.replace("/dashboard")}
              >
                {t("completeProfile.skip", "Passer cette étape")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
